import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Layers, Flame, ArrowRight } from "lucide-react";
import api, { resolveImg } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { StatusBadge } from "../components/StatusBadge";
import { Input } from "../components/ui/input";
import { SubscribeForm } from "../components/SubscribeForm";

export default function Hub() {
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [genre, setGenre] = useState("All");

  useEffect(() => {
    api
      .get("/hub")
      .then((res) => setSeries(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const genres = useMemo(() => {
    const set = new Set();
    series.forEach((s) => (s.genre || "").split("/").forEach((g) => g.trim() && set.add(g.trim())));
    return ["All", ...Array.from(set)];
  }, [series]);

  const filtered = useMemo(() => {
    return series.filter((s) => {
      const matchesQuery = s.title.toLowerCase().includes(query.toLowerCase());
      const matchesGenre = genre === "All" || (s.genre || "").includes(genre);
      return matchesQuery && matchesGenre;
    });
  }, [series, query, genre]);

  const featured = series.find((s) => s.is_featured) || series[0];

  return (
    <div className="min-h-screen bg-[#0B0C10]">
      <Navbar />

      {/* Hero */}
      {featured && (
        <section className="relative overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={resolveImg(featured.banner_image_url || featured.cover_image_url)}
              alt=""
              className="w-full h-full object-cover opacity-40"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C10] via-[#0B0C10]/70 to-[#0B0C10]/30" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0B0C10] via-transparent to-transparent" />
          </div>
          <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-20 sm:pt-24 sm:pb-28">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-2xl"
            >
              <span className="font-mono-kh text-xs uppercase tracking-[0.3em] text-rose-400 flex items-center gap-2">
                <Flame className="w-3.5 h-3.5" /> Featured Series
              </span>
              <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none mt-4 text-white">
                {featured.title}
              </h1>
              <p data-testid="hero-tagline" className="text-base text-gray-300 leading-relaxed mt-5 line-clamp-3 max-w-xl">
                {featured.featured_tagline || featured.synopsis}
              </p>
              <div className="flex items-center gap-3 mt-7">
                <Link
                  to={`/series/${featured.slug}`}
                  data-testid="hero-read-btn"
                  className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-500 text-white font-semibold px-6 py-3 rounded-full transition-all hover:gap-3 glow-primary"
                >
                  Start Reading <ArrowRight className="w-4 h-4" />
                </Link>
                <StatusBadge status={featured.status} />
              </div>
            </motion.div>
          </div>
        </section>
      )}

      <div className="max-w-7xl mx-auto px-5 sm:px-8 pb-14">
        <SubscribeForm source="homepage" className="max-w-3xl" />
      </div>

      {/* Library */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-24 pt-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight text-white flex items-center gap-2.5">
            <Layers className="w-6 h-6 text-rose-500" /> The Library
          </h2>
          <div className="relative w-full sm:w-72">
            <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              data-testid="hub-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search titles..."
              className="pl-9 bg-[#12141D] border-white/10 text-white placeholder:text-gray-500 rounded-full focus-visible:ring-rose-500"
            />
          </div>
        </div>

        {/* Genre pills */}
        <div className="flex flex-wrap gap-2 mb-9">
          {genres.map((g) => (
            <button
              key={g}
              data-testid={`genre-pill-${g.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setGenre(g)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all border ${
                genre === g
                  ? "bg-rose-600 border-rose-600 text-white"
                  : "bg-white/5 border-white/10 text-gray-300 hover:bg-white/10"
              }`}
            >
              {g}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-[3/4] rounded-2xl panel-skeleton" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24 text-gray-500 font-mono-kh">No series found.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {filtered.map((s, i) => (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
              >
                <Link
                  to={`/series/${s.slug}`}
                  data-testid={`series-card-${s.slug}`}
                  className="group block relative rounded-2xl overflow-hidden bg-[#12141D] border border-white/10 hover:border-rose-500/40 transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="aspect-[3/4] overflow-hidden relative">
                    <img
                      src={resolveImg(s.cover_image_url)}
                      alt={s.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
                    <div className="absolute top-3 left-3">
                      <StatusBadge status={s.status} />
                    </div>
                    <div className="absolute bottom-3 right-3 font-mono-kh text-[10px] bg-black/70 backdrop-blur text-cyan-300 px-2 py-1 rounded-md border border-white/10">
                      {s.published_chapters} CH
                    </div>
                  </div>
                  <div className="p-3.5">
                    <h3 className="font-semibold text-white text-sm sm:text-base leading-tight line-clamp-1 group-hover:text-rose-400 transition-colors">
                      {s.title}
                    </h3>
                    <p className="text-xs text-gray-500 mt-1 font-mono-kh uppercase tracking-wide line-clamp-1">
                      {s.genre}
                    </p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
